import React from 'react'
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Collapse,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  ListItemAvatar,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Home,
  Person,
  Groups,
  Folder,
  Description,
  Assessment,
  Logout,
  People,
  ExpandLess,
  ExpandMore,
  Notifications,
  VideoCall,
  Movie,
  BarChart,
  Gavel,
  EmojiEvents,
  MoreVert,
  AccountBalance,
  AssignmentTurnedIn,
  AssignmentInd,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { logout } from '../store/slices/authSlice'
import { apiSlice } from '../store/api/apiSlice'
import { hasPermission, getRoleLabel, type Permission } from '../utils/permissions'
import { useGetUnreadCountQuery, useGetNotificationsQuery, useMarkAllAsReadMutation } from '../store/api/notificationsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import Logo from './Logo'
import GlobalDeadlineBanner from './GlobalDeadlineBanner'

const drawerWidth = 260

// ─── Sidebar structure ──────────────────────────────────────────────────────

/**
 * L1 (audit 2026-05-21): student tab gating theo competition stage.
 * - 'r0' = team chưa duyệt → chỉ Trang chủ + Đội thi + Cuộc thi + Thông báo + Profile
 * - 'r1' = team `active` đã được duyệt → + Hồ sơ khởi kiện + Video giới thiệu + Thành tích
 * - 'r2' = team `competition_status === 'advanced_to_semifinal'` → + Phiên toà ảo + Bảng phân vai
 * - 'r3' = team `competition_status === 'advanced_to_final'` (Phase 5 2026-05-25)
 *         → kế thừa r2 + có thể thêm shortcut Vòng Chung kết
 */
type StudentStage = 'r0' | 'r1' | 'r2' | 'r3'
const STAGE_ORDER: Record<StudentStage, number> = { r0: 0, r1: 1, r2: 2, r3: 3 }

interface MenuLeaf {
  text: string
  icon: React.ReactNode
  path: string
  permission?: Permission
  hideIfRole?: string
  allowedRoles?: string[]
  /** Min student stage required to see this item. Chỉ áp dụng khi role='student'. */
  studentStageRequired?: StudentStage
}

interface MenuSection {
  key: string
  label: string
  items: MenuLeaf[]
}

// Top-level standalone item (Trang chủ — không thuộc section nào)
const topLevelItems: MenuLeaf[] = [
  { text: 'Trang chủ', icon: <Home />, path: '/dashboard' },
]

// YC17 (audit 2026-05-23): Judge chỉ chấm điểm ở R2 Phiên toà ảo. Ẩn các
// tab không liên quan — chỉ giữ: Đội thi (info), Cuộc thi, Phiên toà ảo.
// Chấm điểm thực hiện ngay trong tab Phiên toà ảo, KHÔNG cần mở tab Chấm
// điểm riêng — vẫn để route /scoring tồn tại nhưng ẩn khỏi menu cho judge.
const sections: MenuSection[] = [
  {
    key: 'manage',
    label: 'QUẢN LÝ',
    items: [
      { text: 'Người dùng', icon: <People />, path: '/users', permission: 'MANAGE_USERS' },
      // YC17: judge cần xem thông tin đội → KHÔNG còn hideIfRole='judge'
      { text: 'Đội thi', icon: <Groups />, path: '/teams' },
      { text: 'Cuộc thi', icon: <Folder />, path: '/cases' },
      {
        text: 'Phân công GK',
        icon: <Gavel />,
        path: '/judge-assignments',
        // YC17: judge KHÔNG thấy Phân công GK
        allowedRoles: ['secretary', 'support'],
      },
    ],
  },
  {
    key: 'dossier',
    label: 'HỒ SƠ DỰ THI',
    items: [
      // L1: student R1+ (đội đã duyệt) mới thấy. YC17: judge cũng ẩn.
      { text: 'Hồ sơ khởi kiện', icon: <Description />, path: '/dossier', hideIfRole: 'judge', studentStageRequired: 'r1' },
      // YC17: judge KHÔNG thấy Video giới thiệu
      { text: 'Video giới thiệu', icon: <Movie />, path: '/intro-video', hideIfRole: 'judge', studentStageRequired: 'r1' },
      // YC17: judge KHÔNG thấy Bảng phân vai
      { text: 'Bảng phân vai', icon: <AssignmentInd />, path: '/role-sheets', hideIfRole: 'judge', studentStageRequired: 'r2' },
    ],
  },
  {
    key: 'session',
    label: 'PHIÊN XỬ',
    items: [
      // L1: student R2 (advanced_to_semifinal) mới thấy. Judge xem để chấm.
      { text: 'Phiên toà ảo', icon: <VideoCall />, path: '/courtroom', studentStageRequired: 'r2' },
      { text: 'Thư ký phiên', icon: <AccountBalance />, path: '/secretary-desk', allowedRoles: ['secretary'] },
      {
        text: 'Tuyên án',
        icon: <AssignmentTurnedIn />,
        path: '/verdicts-review',
        allowedRoles: ['secretary', 'support'],
      },
    ],
  },
  {
    key: 'results',
    label: 'KẾT QUẢ',
    items: [
      // 2026-05-25: Hiện lại "Chấm điểm" cho judge — Bán kết / Chung kết
      // dùng phiếu auto-spawn khi courtroom kết thúc; judge cần entry point
      // rõ ràng để vào nhập điểm trong deadline (48h cho R2, 2h cho R3).
      // (YC17 cũ ẩn vì kỳ vọng "chấm trực tiếp trong Phiên toà ảo" nhưng UI
      // đó chưa được build — gây lỗi user không có chỗ chấm.)
      { text: 'Chấm điểm', icon: <Assessment />, path: '/scoring', permission: 'SCORE_CASES' },
      // L1: student R1+ (đội duyệt) mới thấy Thành tích
      { text: 'Thành tích', icon: <EmojiEvents />, path: '/achievements', allowedRoles: ['student', 'secretary'], studentStageRequired: 'r1' },
      {
        text: 'Báo cáo & phân tích',
        icon: <BarChart />,
        path: '/reports',
        // YC17: judge KHÔNG thấy Báo cáo & phân tích
        allowedRoles: ['secretary', 'support'],
      },
    ],
  },
]

// ─── Visibility helper ─────────────────────────────────────────────────────

function leafVisible(
  item: MenuLeaf,
  role: string | undefined,
  studentStage: StudentStage | null,
): boolean {
  if (!role) return false
  if (item.allowedRoles && !item.allowedRoles.includes(role)) return false
  if (item.permission && !hasPermission(role, item.permission)) return false
  if (item.hideIfRole && role === item.hideIfRole) return false
  // L1: student stage gating
  if (role === 'student' && item.studentStageRequired && studentStage) {
    if (STAGE_ORDER[studentStage] < STAGE_ORDER[item.studentStageRequired]) {
      return false
    }
  }
  return true
}

function sectionVisibleItems(
  section: MenuSection,
  role: string | undefined,
  studentStage: StudentStage | null,
): MenuLeaf[] {
  return section.items.filter((it) => leafVisible(it, role, studentStage))
}

// ─── Layout ────────────────────────────────────────────────────────────────

export default function Layout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)

  // L1: tính student stage từ team membership + competition_status.
  const { data: teamsList = [] } = useGetTeamsQuery(undefined, { skip: !isAuthenticated })
  const studentStage = React.useMemo<StudentStage | null>(() => {
    if (user?.role !== 'student') return null
    const team = teamsList.find((t) => t.members?.includes(user.id))
    if (!team) return 'r0'
    const status = (team as any).status
    if (status !== 'active' && status !== 'auto_approved') return 'r0'
    const compStatus = (team as any).competition_status
    // Phase 5 (2026-05-25): handle r3 (advanced_to_final). r3 inherits mọi
    // menu của r2 + visible cho mọi item có studentStageRequired ≤ r3.
    if (compStatus === 'advanced_to_final') return 'r3'
    if (compStatus === 'advanced_to_semifinal') return 'r2'
    return 'r1'
  }, [user?.role, user?.id, teamsList])

  // Notification state
  const [notificationAnchor, setNotificationAnchor] = React.useState<null | HTMLElement>(null)
  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null)
  const [locallyCheckedCount, setLocallyCheckedCount] = React.useState<number | null>(null)
  const [markAllAsRead] = useMarkAllAsReadMutation()

  const { data: unreadCountData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 20000,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const { data: recentNotifications = [] } = useGetNotificationsQuery(
    { limit: 5 },
    {
      pollingInterval: 20000,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const rawUnreadCount = unreadCountData?.count || 0
  const unreadCount =
    locallyCheckedCount !== null && rawUnreadCount <= locallyCheckedCount ? 0 : rawUnreadCount

  React.useEffect(() => {
    if (rawUnreadCount === 0) {
      setLocallyCheckedCount(null)
    } else if (locallyCheckedCount !== null && rawUnreadCount > locallyCheckedCount) {
      setLocallyCheckedCount(null)
    }
  }, [locallyCheckedCount, rawUnreadCount])

  const acknowledgeNotifications = React.useCallback(async () => {
    if (rawUnreadCount <= 0) return
    setLocallyCheckedCount(rawUnreadCount)
    try {
      await markAllAsRead().unwrap()
    } catch (error) {
      setLocallyCheckedCount(null)
      console.error('Mark notifications as read failed:', error)
    }
  }, [markAllAsRead, rawUnreadCount])

  // ─── Accordion section state ────────────────────────────────────────────
  // Auto-pick section chứa active route làm initial expanded.
  // Bug fix 2026-05-21: khi route active là top-level item (vd /dashboard) —
  // không section nào match → fallback mở section đầu tiên có items visible.
  // Tránh case user mới login chỉ thấy 4 section headers collapsed, tưởng thiếu menu.
  const initialOpenSection = React.useMemo<string | null>(() => {
    for (const s of sections) {
      if (s.items.some((it) => it.path === location.pathname)) return s.key
    }
    // Fallback: mở section đầu tiên có ít nhất 1 item visible cho user role hiện tại.
    for (const s of sections) {
      const visible = sectionVisibleItems(s, user?.role, studentStage)
      if (visible.length > 0) return s.key
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, studentStage])

  const [openSection, setOpenSection] = React.useState<string | null>(initialOpenSection)
  // Đồng bộ state khi initialOpenSection re-compute (vd: role/team thay đổi)
  React.useEffect(() => {
    setOpenSection((cur) => cur ?? initialOpenSection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenSection])

  // Khi route đổi → nếu section chứa route mới khác section đang mở → auto-switch
  React.useEffect(() => {
    const activeSection = sections.find((s) =>
      s.items.some((it) => it.path === location.pathname),
    )
    if (activeSection && activeSection.key !== openSection) {
      setOpenSection(activeSection.key)
    }
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (key: string) => {
    setOpenSection((prev) => (prev === key ? null : key))
  }

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleLogout = () => {
    setProfileAnchor(null)
    dispatch(logout())
    // 2026-05-27: clear RTK Query cache khi logout — tránh user kế tiếp
    // login thấy data cache của user cũ.
    dispatch(apiSlice.util.resetApiState())
    navigate('/login')
  }

  const handleNotificationClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget)
    acknowledgeNotifications()
  }
  const handleNotificationClose = () => setNotificationAnchor(null)
  const handleNotificationItemClick = () => {
    handleNotificationClose()
    acknowledgeNotifications()
    navigate('/notifications')
  }

  const handleNavigate = (path: string) => {
    if (path === '/notifications') acknowledgeNotifications()
    navigate(path)
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // ─── Render helpers ─────────────────────────────────────────────────────

  const renderLeaf = (item: MenuLeaf, isChild = false) => {
    const isActive = location.pathname === item.path
    return (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={isActive}
          onClick={() => handleNavigate(item.path)}
          sx={{
            mx: 1,
            mb: 0.5,
            ml: isChild ? 2 : 1,
            borderRadius: 2,
            '&.Mui-selected': {
              background:
                'linear-gradient(90deg, rgba(20, 78, 140, 0.12) 0%, rgba(246, 65, 131, 0.1) 100%)',
              color: 'primary.main',
              borderLeft: '4px solid',
              borderColor: 'secondary.main',
              '&:hover': {
                background:
                  'linear-gradient(90deg, rgba(20, 78, 140, 0.16) 0%, rgba(246, 65, 131, 0.14) 100%)',
              },
              '& .MuiListItemIcon-root': { color: 'primary.main' },
            },
            '&:hover': { backgroundColor: 'rgba(20, 78, 140, 0.06)' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: isActive ? 'primary.main' : 'inherit' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: isActive ? 600 : 400,
              fontSize: '0.92rem',
            }}
          />
        </ListItemButton>
      </ListItem>
    )
  }

  const renderSection = (section: MenuSection) => {
    const visibleItems = sectionVisibleItems(section, user?.role, studentStage)
    if (visibleItems.length === 0) return null
    const isOpen = openSection === section.key
    const hasActiveChild = visibleItems.some((it) => it.path === location.pathname)

    return (
      <React.Fragment key={section.key}>
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton
            onClick={() => toggleSection(section.key)}
            sx={{
              mx: 1,
              borderRadius: 2,
              py: 0.75,
              '&:hover': { backgroundColor: 'rgba(20, 78, 140, 0.06)' },
            }}
          >
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: 1.2,
                color: hasActiveChild ? 'primary.main' : 'text.secondary',
                textTransform: 'uppercase',
              }}
            />
            {isOpen ? (
              <ExpandLess fontSize="small" sx={{ color: 'text.secondary' }} />
            ) : (
              <ExpandMore fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={isOpen} timeout={200} unmountOnExit>
          <List component="div" disablePadding>
            {visibleItems.map((it) => renderLeaf(it, true))}
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: '0 2px 14px rgba(14, 58, 102, 0.38)',
        }}
      >
        <Toolbar sx={{ color: 'inherit' }}>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: 0.3 }}
          >
            Gender Justice Moot Court
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={!isMobile}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: '2px 0 16px rgba(20, 78, 140, 0.08)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Logo variant="sidebar" />
        <Divider />

        {/* Main nav (scrollable) */}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List sx={{ pt: 1 }}>
            {/* Top-level items (Trang chủ) */}
            {topLevelItems.map((it) => renderLeaf(it, false))}

            {/* Sections accordion */}
            {sections.map(renderSection)}
          </List>
        </Box>

        {/* Footer pinned */}
        <Divider />
        <Box sx={{ p: 1.25 }}>
          {/* Notification row */}
          <ListItemButton
            onClick={handleNotificationClick}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
              <Badge
                badgeContent={unreadCount > 99 ? '99+' : unreadCount || null}
                color="secondary"
                overlap="circular"
              >
                <Notifications />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary="Thông báo"
              primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 500 }}
            />
          </ListItemButton>

          {/* User row */}
          {user && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 1,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.06),
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.92rem',
                  fontWeight: 700,
                }}
              >
                {(user.full_name || user.username || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {user.full_name || user.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                >
                  {getRoleLabel(user.role)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => setProfileAnchor(e.currentTarget)}
                aria-label="Tuỳ chọn tài khoản"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Notification popover */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{ sx: { width: 360, maxHeight: 400 } }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Thông báo</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadCount} thông báo chưa đọc
              </Typography>
            )}
          </Box>
          {recentNotifications.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                Không có thông báo nào
              </Typography>
            </MenuItem>
          ) : (
            recentNotifications.map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={handleNotificationItemClick}
                sx={{
                  whiteSpace: 'normal',
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {notification.message.length > 100
                      ? `${notification.message.substring(0, 100)}...`
                      : notification.message}
                  </Typography>
                  {notification.metadata?.meeting_link && (
                    <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                      🔗 Có link phòng trực tuyến
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))
          )}
          <Divider />
          <MenuItem onClick={handleNotificationItemClick}>
            <Typography variant="body2" color="primary" sx={{ textAlign: 'center', width: '100%' }}>
              Xem tất cả thông báo
            </Typography>
          </MenuItem>
        </Menu>

        {/* Profile / Logout popover */}
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={() => setProfileAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setProfileAnchor(null)
              navigate('/profile')
            }}
          >
            <ListItemAvatar sx={{ minWidth: 36 }}>
              <Person fontSize="small" />
            </ListItemAvatar>
            <ListItemText primary="Thông tin cá nhân" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemAvatar sx={{ minWidth: 36 }}>
              <Logout fontSize="small" />
            </ListItemAvatar>
            <ListItemText primary="Đăng xuất" />
          </MenuItem>
        </Menu>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100dvh',
          bgcolor: 'background.default',
          p: { xs: 2, sm: 3 },
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Toolbar />
        <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -3, mb: 3 }}>
          <GlobalDeadlineBanner />
        </Box>
        <Outlet />
      </Box>
    </Box>
  )
}
